import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

const CHECK_EVERY_MS = 60 * 1000;
const bundleName = (html) => (html.match(/\/static\/js\/(main\.[a-z0-9]+\.js)/) || [])[1];

// Browsers keep serving a cached index.html after a deploy, so the new JavaScript
// is never picked up without a hard refresh. This compares the bundle this tab
// loaded with the one in a freshly fetched index.html and reloads the page as soon
// as a newer one exists.
const UpdateBanner = () => {
  const [stuckOn, setStuckOn] = useState(null);

  useEffect(() => {
    const loaded = document.querySelector('script[src*="/static/js/main."]');
    const runningBundle = loaded && bundleName(loaded.src); // absent in the dev server - nothing to check
    if (!runningBundle) return undefined;

    const check = async () => {
      try {
        const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = bundleName(await res.text());
        if (!latest || latest === runningBundle) return;

        // Reload once per new version. If the reload somehow still serves the old
        // page, show a manual button instead of looping.
        if (sessionStorage.getItem('autoReloadedFor') === latest) {
          setStuckOn(latest);
          return;
        }
        sessionStorage.setItem('autoReloadedFor', latest);
        window.location.reload();
      } catch {
        // offline or a blip - try again next time
      }
    };

    check();
    const timer = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!stuckOn) return null;

  return (
    <div className="no-print fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] bg-slate-900 text-white rounded-2xl shadow-xl px-5 py-3 flex items-center gap-4 text-sm">
      <span>A new version is available.</span>
      <button
        onClick={() => window.location.reload()}
        className="flex items-center gap-1.5 bg-white text-slate-900 font-bold px-3 py-1.5 rounded-xl hover:bg-slate-100"
      >
        <RefreshCw className="w-4 h-4" /> Refresh now
      </button>
    </div>
  );
};

export default UpdateBanner;
